import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styles from './scrollable-modal-content.module.scss';
import { classNames } from 'tradera/utils/class-names';
import { HeaderWithCloseButton } from './header-with-close-button';

export type ScrollableModalContentProps = {
    children: React.ReactNode;
    footer?: React.ReactNode;
    /**
     * Just want a HeaderWithCloseButton? Use `title` instead.
     */
    header?: React.ReactNode;
    /**
     * If title is provided, a HeaderWithCloseButton along with it will be rendered.
     * Remember to pass `onClose` as well!
     */
    title?: string;
    className?: string;
    innerClassName?: string;
    /**
     * If true, the content will be rendered with a higher z-index than the footer.
     */
    forceZIndex?: boolean;
    onClose?: () => void;
    noPadding?: boolean;
};

export const ScrollableModalContent = ({
    children,
    footer,
    header,
    className,
    title,
    onClose,
    forceZIndex,
    innerClassName = '',
    noPadding
}: ScrollableModalContentProps) => {
    const inViewOptions = { initialInView: true, threshold: 1 };
    const [scrollableContentRef, isScrollableContentEntirelyVisible] =
        useInView(inViewOptions);
    const [hasScroll, setHasScroll] = useState(false);

    useEffect(() => {
        setHasScroll(!isScrollableContentEntirelyVisible);
    }, [isScrollableContentEntirelyVisible]);

    const head = header ? (
        header
    ) : title ? (
        <HeaderWithCloseButton onClose={onClose} title={title} border />
    ) : null;

    return (
        <div
            className={classNames(
                `w-100 h-100 d-flex flex-column position-relative`,
                className
            )}>
            {head}
            <div
                className={classNames(
                    `d-flex flex-column overflow-auto flex-grow-1`,
                    innerClassName,
                    styles.smallScrollbar
                )}
                style={forceZIndex ? { zIndex: 1 } : {}}>
                <div
                    ref={scrollableContentRef}
                    className={classNames('d-flex flex-column flex-grow-1', {
                        'p-0': noPadding,
                        'pt-2 pt-sm-3 pb-1': !noPadding,
                        'pb-2': hasScroll && !noPadding,
                        'pb-2 pb-sm-3': !footer && !noPadding,
                        'px-2 px-sm-3': !noPadding
                    })}>
                    {children}
                </div>
            </div>
            {footer && (
                <div
                    className={classNames(
                        `flex-shrink-0 d-flex flex-wrap justify-content-between`,
                        {
                            [styles.shadow]: hasScroll,
                            ['p-2 p-sm-3']: !noPadding
                        },
                        styles.footer
                    )}>
                    {footer}
                </div>
            )}
        </div>
    );
};
